import { API_URL } from '@/config/app';
import { baseApi } from '@/store/api/baseApi';
import type { RootState } from '@/store';
import type {
  BulkProductIdsPayload,
  BulkProductStatusPayload,
  CreateProductPayload,
  PaginatedProducts,
  Product,
  ProductImportResult,
  ProductListParams,
  UpdateProductPayload,
} from '@/types/product';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildProductListQuery(params: ProductListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'name');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.productTypes?.length) searchParams.set('productTypes', params.productTypes.join(','));
  if (params.brandIds?.length) searchParams.set('brandIds', params.brandIds.join(','));
  if (params.categoryIds?.length) searchParams.set('categoryIds', params.categoryIds.join(','));
  if (params.imprintValueIds?.length) {
    searchParams.set('imprintValueIds', params.imprintValueIds.join(','));
  }
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const productsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<PaginatedProducts, ProductListParams>({
      query: (params) => ({
        url: `/products/manage?${buildProductListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildProductListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildProductListQuery(currentArg ?? {}) !== buildProductListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Products' as const, id })),
              { type: 'Products', id: 'LIST' },
            ]
          : [{ type: 'Products', id: 'LIST' }],
    }),
    getProduct: builder.query<Product, string>({
      query: (id) => `/products/manage/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Products', id }],
    }),
    createProduct: builder.mutation<Product, CreateProductPayload>({
      query: (body) => ({ url: '/products/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Products', id: 'LIST' }],
    }),
    updateProduct: builder.mutation<Product, { id: string; body: UpdateProductPayload }>({
      query: ({ id, body }) => ({ url: `/products/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Products', id },
        { type: 'Products', id: 'LIST' },
      ],
    }),
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({ url: `/products/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Products', id: 'LIST' }],
    }),
    bulkUpdateProductStatus: builder.mutation<{ count: number }, BulkProductStatusPayload>({
      query: (body) => ({ url: '/products/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Products', id: 'LIST' }],
    }),
    bulkDeleteProducts: builder.mutation<{ count: number }, BulkProductIdsPayload>({
      query: (body) => ({ url: '/products/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Products', id: 'LIST' }],
    }),
    importProducts: builder.mutation<ProductImportResult, File>({
      async queryFn(file, api) {
        const { accessToken } = (api.getState() as RootState).auth;
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await fetch(`${API_URL}/products/import`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Product import failed.',
              },
            };
          }

          const payload = isApiResponse<ProductImportResult>(json) ? json.data : (json as ProductImportResult);
          const message = isApiResponse<ProductImportResult>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Product import failed.',
            },
          };
        }
      },
      invalidatesTags: [{ type: 'Products', id: 'LIST' }],
    }),
    uploadSamplePdf: builder.mutation<{ samplePdfUrl: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/products/upload-sample-pdf`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Sample PDF upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ samplePdfUrl: string }>(json)
            ? json.data
            : (json as { samplePdfUrl: string });
          const message = isApiResponse<{ samplePdfUrl: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Sample PDF upload failed.',
            },
          };
        }
      },
    }),
    uploadContentPdf: builder.mutation<{ contentPdfUrl: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/products/upload-content-pdf`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Content PDF upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ contentPdfUrl: string }>(json)
            ? json.data
            : (json as { contentPdfUrl: string });
          const message = isApiResponse<{ contentPdfUrl: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Content PDF upload failed.',
            },
          };
        }
      },
    }),
    uploadDigitalSamplePdf: builder.mutation<{ digitalSamplePdfUrl: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/products/upload-digital-sample-pdf`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data:
                  getRtkErrorMessage({ data: json }) ??
                  'Sample for eFLIPBOOK and WEBBASED upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ digitalSamplePdfUrl: string }>(json)
            ? json.data
            : (json as { digitalSamplePdfUrl: string });
          const message = isApiResponse<{ digitalSamplePdfUrl: string }>(json)
            ? json.message
            : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Sample for eFLIPBOOK and WEBBASED upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkUpdateProductStatusMutation,
  useBulkDeleteProductsMutation,
  useImportProductsMutation,
  useUploadSamplePdfMutation,
  useUploadContentPdfMutation,
  useUploadDigitalSamplePdfMutation,
} = productsApi;
